// ── Column / Config ──────────────────────────────────────────────────

export type CsvColumnDefinition = {
  key: string;
  title: string;
  required: boolean;
  /** Optional regex pattern for cell-level validation (csv_matchy Condition) */
  regex?: string;
  /** Custom error message shown when the regex fails */
  regexMessage?: string;
  /**
   * csv_matchy FieldType override.
   * Defaults to 'string' when omitted.
   * Accepted values: 'float' | 'integer' | 'string' | 'bool'
   */
  fieldType?: 'float' | 'integer' | 'string' | 'bool' | 'stringArray';
  /**
   * If true, this column will not be expected or included in the CSV file,
   * but will still appear in the CRUD table.
   */
  excludeFromCsv?: boolean;
  /**
   * If provided, renders a dropdown/select in the CRUD table instead of a text input.
   * Array of allowed values for this field.
   */
  options?: string[];
};

export type CsvFileConfig = {
  name: string;
  description: string;
  columns: CsvColumnDefinition[];
  fileName: string;
  hideUpload?: boolean;
  readonly?: boolean;
};

// ── Parsed data ─────────────────────────────────────────────────────

export type ParsedCsvRow = Record<string, string>;

// ── Upload state (per tab) ──────────────────────────────────────────

export type CsvFileUploadState = {
  file: File | null;
  status: 'idle' | 'parsing' | 'success' | 'error';
  data: ParsedCsvRow[];
  errors: string[];
  fileName: string;
};

// ── CRUD row (rows persisted in DB) ─────────────────────────────────

export type LookupRow = Record<string, unknown> & { _key: string };

export type LookupTabData = {
  rows: LookupRow[];
  loading: boolean;
  error: string | null;
};

// ── View mode toggle ────────────────────────────────────────────────

export type TabViewMode = 'crud' | 'upload' | 'history';

// ── Component props ─────────────────────────────────────────────────

export type ComponentSchemaManagerProps = {
  fileConfigs: CsvFileConfig[];
  activeTab: string;
  viewModes: Record<string, TabViewMode>;
  uploads: Record<string, CsvFileUploadState>;
  lookups: Record<string, LookupTabData>;
  onTabChange: (key: string) => void;
  onToggleView: (configName: string, viewMode: TabViewMode) => void;
  // Upload actions
  onFileUpload: (configName: string, file: File) => void;
  onFileRemove: (configName: string) => void;
  onSubmit: (configName: string) => void;
  onDownloadTemplate: (configName: string) => void;
  submitting: Record<string, boolean>;
  // CRUD actions
  onAddRow: (configName: string) => void;
  onEditRow: (configName: string, key: string, row: ParsedCsvRow) => void;
  onDeleteRow: (configName: string, key: string) => void;
  onSaveRow: (configName: string, key: string) => void;
  onCancelEdit: (configName: string, key: string) => void;
  editingRows: Record<string, Record<string, ParsedCsvRow>>;
  onRefresh: (configName: string) => void;
  onApplySynonyms?: (
    configName: string,
    key: string,
    synonyms: string[],
  ) => void | Promise<void>;
  onMatchySubmit: (configName: string, data: ParsedCsvRow[]) => void;
  // Associations
  associations: Record<string, AssociationMap>;
  transferModal: TransferModalState;
  onOpenTransfer: (
    configName: string,
    id: string,
    code: string,
    name: string,
    targetConfigName?: string,
  ) => void;
  onCloseTransfer: () => void;
  onSaveTransfer: (targetKeys: string[]) => void;
  // Pagination
  pagination?: Record<
    string,
    { current: number; pageSize: number; total?: number }
  >;
  onPageChange?: (configName: string, page: number, pageSize: number) => void;
  onSearch?: (configName: string, q: string) => void;
  searchTerms?: Record<string, string>;
};

export type CsvPreviewTableProps = {
  columns: CsvColumnDefinition[];
  data: ParsedCsvRow[];
  errors: string[];
  status: CsvFileUploadState['status'];
};

export type CsvDropZoneProps = {
  config: CsvFileConfig;
  uploadState: CsvFileUploadState;
  onUpload: (file: File) => void;
  onRemove: () => void;
  onDownloadTemplate: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
};

export type CrudTableProps = {
  config: CsvFileConfig;
  data: LookupRow[];
  loading: boolean;
  error: string | null;
  editingRows: Record<string, ParsedCsvRow>;
  onAddRow: () => void;
  onEditRow: (key: string, row: ParsedCsvRow) => void;
  onDeleteRow: (key: string) => void;
  onSaveRow: (key: string) => void;
  onCancelEdit: (key: string) => void;
  onRefresh: () => void;
  onApplySynonyms?: (key: string, synonyms: string[]) => void | Promise<void>;
  // Associations
  associationCounts?: Record<string, number>;
  associationLabel?: string;
  onOpenTransfer?: (
    id: string,
    code: string,
    name: string,
    targetConfigName?: string,
  ) => void;
  // Secondary associations (e.g. Subsystem <-> Component Class)
  associationCountsSecondary?: Record<string, number>;
  associationLabelSecondary?: string;
  onOpenTransferSecondary?: (
    id: string,
    code: string,
    name: string,
    targetConfigName?: string,
  ) => void;
  // Pagination
  pagination?: { current: number; pageSize: number; total?: number };
  onPageChange?: (page: number, pageSize: number) => void;
  search?: string;
  onSearch?: (q: string) => void;
};

export type AssociationMap = Record<string, string[]>;

export type TransferModalState = {
  open: boolean;
  sourceConfigName: string;
  sourceId: string;
  sourceCode: string;
  sourceName: string;
  targetConfigName?: string;
};

export type TabContentProps = {
  config: CsvFileConfig;
  viewMode: TabViewMode;
  onToggleView: (viewMode: TabViewMode) => void;
  // Upload
  uploadState: CsvFileUploadState;
  onFileUpload: (file: File) => void;
  onFileRemove: () => void;
  onSubmit: () => void;
  onDownloadTemplate: () => void;
  isSubmitting: boolean;
  // CRUD
  lookupData: LookupTabData;
  editingRows: Record<string, ParsedCsvRow>;
  onAddRow: () => void;
  onEditRow: (key: string, row: ParsedCsvRow) => void;
  onDeleteRow: (key: string) => void;
  onSaveRow: (key: string) => void;
  onCancelEdit: (key: string) => void;
  onRefresh: () => void;
  onApplySynonyms?: (key: string, synonyms: string[]) => void | Promise<void>;
  // Matchy
  onMatchySubmit: (data: ParsedCsvRow[]) => void;
  // Associations
  associationCounts?: Record<string, number>;
  onOpenTransfer?: (
    id: string,
    code: string,
    name: string,
    targetConfigName?: string,
  ) => void;
  // Secondary associations (optional)
  // (removed duplicate secondary association props)
  associationLabel?: string;
  associationCountsSecondary?: Record<string, number>;
  associationLabelSecondary?: string;
  onOpenTransferSecondary?: (
    id: string,
    code: string,
    name: string,
    targetConfigName?: string,
  ) => void;
  targetConfigName?: string;
  // Pagination
  pagination?: { current: number; pageSize: number; total?: number };
  onPageChange?: (page: number, pageSize: number) => void;
  search?: string;
  onSearch?: (q: string) => void;
};

export enum ConfigName {
  ASSET_TYPE = 'Asset Type',
  SUBSYSTEM = 'Subsystem',
  COMPONENT_CLASS = 'Component Class',
  COMPONENT_TYPE = 'Component Type',
  MANUFACTURER = 'Manufacturer',
  ASSET_MODEL = 'Asset Model',
  ASSET_TYPE_MANUFACTURER_MODEL = 'Asset Type Manufacturer Model',
  ASSET_SUBSYSTEM_COMPONENT_CLASS_MAPPING = 'Asset-Subsystem-ComponentClass Mapping',
  ASSET_SUBSYSTEM_COMPONENT_CLASS_COMPONENT_TYPE_MAPPING = 'Asset-Subsystem-ComponentClass-ComponentType Mapping',
}

export const CONFIG_NAMES = [
  ConfigName.ASSET_TYPE,
  ConfigName.SUBSYSTEM,
  ConfigName.COMPONENT_CLASS,
  ConfigName.COMPONENT_TYPE,
  ConfigName.MANUFACTURER,
  ConfigName.ASSET_MODEL,
  ConfigName.ASSET_TYPE_MANUFACTURER_MODEL,
  ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_MAPPING,
  ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_COMPONENT_TYPE_MAPPING,
] as const;

export const SUBSYSTEM_SECONDARY_KEY = 'SubsystemSecondary';

export type ComponentSchemaManagerViewProps = {
  tabItems: Array<{
    key: string;
    label: React.ReactNode;
    children: React.ReactNode;
  }>;
  activeTab: string;
  onTabChange: (key: string) => void;
  transferModal: TransferModalState;
  transferTargetItems: LookupRow[];
  transferSelectedKeys: string[];
  onSaveTransfer: (targetKeys: string[]) => void;
  onCloseTransfer: () => void;
  atmmCreateOpen: boolean;
  onCloseAtmmCreate: () => void;
  onAtmmCreateSuccess: () => void;
};
